import http from '../../shared/http.js';
import { withFlash } from '../../shared/flash.js';
import { bindFormModule } from '../../shared/formBinding.js';
import { formModalSaved, isFormModalOpen } from '../../shared/formModal.js';
import { hideLoading, notifyError, notifySuccess, showLoading } from '../../shared/notify.js';

// Creating and editing a candidate, from the full page and from the shared
// dialog alike - bindFormModule handles both, so nothing here knows which it is
// on.

// Optional free-text fields. An empty one is sent as '' rather than dropped:
// dropping a key means "leave unchanged" on a PATCH, so clearing an email in
// the form would otherwise never reach the record.
const NULLABLE_TEXT = ['intranetKandidatId', 'email', 'telefon', 'beruf', 'notiz'];

// Turns the form into the JSON body the API expects.
function readForm(form) {
  const data = Object.fromEntries(new FormData(form).entries());

  // An unchecked toggle is not submitted at all, so the absent key is the
  // "false" the server needs to see - FormData cannot express it.
  data.aktiv = form.querySelector('#aktiv')?.checked ?? true;

  NULLABLE_TEXT.forEach((field) => {
    if (data[field] !== undefined) data[field] = data[field].trim();
  });

  // The owner is three-valued where the other refs are two, and the difference
  // matters on a create: an explicit '' means "nobody, visible to everyone",
  // while an *absent* key is what makes the server default to the creator. So
  // the empty string is sent as null rather than dropped - dropping it would
  // silently assign the candidate to whoever filled the form in, which is the
  // opposite of what picking "Niemand" asked for.
  if (data.verantwortlichBenutzer === '') data.verantwortlichBenutzer = null;

  return data;
}

async function submit(form) {
  const submitButton = form.querySelector('button[type="submit"]');
  const { kandidatId } = form.dataset;
  const data = readForm(form);

  if (submitButton) submitButton.disabled = true;
  showLoading('Wird gespeichert …');

  try {
    const { data: body } = kandidatId
      ? await http.patch(`/kandidaten/${kandidatId}`, data)
      : await http.post('/kandidaten', data);

    // Editing in the dialog stays where it was: the page behind it - the roster
    // or the candidate's own page - is already the right place, so it closes and
    // refetches rather than navigating.
    if (kandidatId && isFormModalOpen()) {
      hideLoading();
      // Before formModalSaved, not after: the caller's onSaved may navigate -
      // a detail page reloads itself - and Notiflix then paints into a document
      // that is being torn down, which throws into the catch below and shows an
      // error for a save that worked.
      notifySuccess('Kandidat wurde aktualisiert.');
      formModalSaved();
      return;
    }

    // Creating goes to the new candidate, the same call kundeForm makes: it is
    // the record somebody is about to keep working on - attaching a CV, writing
    // the first note - rather than a row to admire in the list.
    const id = kandidatId || body.data.kandidat.id;
    window.location.href = withFlash(
      `/kandidaten/${id}`,
      kandidatId ? 'kandidat-aktualisiert' : 'kandidat-erstellt'
    );
  } catch (err) {
    hideLoading();
    if (submitButton) submitButton.disabled = false;
    notifyError(
      err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.'
    );
  }
}

export function initKandidatForm() {
  bindFormModule('kandidat-form', submit);
}
